import {
  LOCAL_SETTINGS_WITH_DEFAULTS,
  LOCAL_SETTINGS_WITH_DEFAULTS_SPECIFIC_TO_EPUB,
} from '../../constants';

/**
 * This method returns a proxy object that provides a simple interface for interacting with settings stored in local storage.
 * It only needs to be run once for the app to get the object, e.g. const localSettings = getLocalSettingsProxy(bookPk);
 *
 * Very straightforward:
 * get a value: console.log(localSettings.my_setting);
 * set a value: localSettings.my_settings = 'new value';
 *
 * @param {Integer} bookPk - the book pk to use to key local setting values that don't apply to all epubs (optional, only if you need epub-specific settings)
 * @returns {Proxy} a proxy object to manage local settings values
 */
const getLocalSettingsProxy = (bookPk = null) => {
  function makeKey() {
    const nameString = Array.from(arguments).join('.');
    const keyedByBookPk = Object.keys(
      LOCAL_SETTINGS_WITH_DEFAULTS_SPECIFIC_TO_EPUB,
    ).includes(nameString.split('.')[0]);

    if (keyedByBookPk && bookPk === null) {
      throw new Error(
        'This local settings proxy instance was not configured to support epub-specific settings.',
      );
    }

    // non-epub-specific configuration will use 0 in place of an epub id here
    return `local_setting:${keyedByBookPk ? bookPk : 0}:${nameString}`;
  }

  function setValue(key, value) {
    if (value !== null) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  }

  function getValue(key, getFallback) {
    const value = localStorage.getItem(key);

    if (value === null) {
      return getFallback() ?? null;
    }

    if (!isNaN(value)) {
      let numberValue = parseFloat(value);
      let numberValueAsInt = parseInt(numberValue);

      // If it's round, just return an int.
      if (numberValueAsInt === numberValue) {
        return numberValueAsInt;
      }

      return numberValue;
    }

    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }

    return value;
  }

  return new Proxy(
    {}, // because this interfaces with localStorage, it does not actually need to proxy to an existing object
    {
      get(_, name) {
        // If the value is configured as an object in the defaults config, return another proxy to handle the nested values
        if (
          typeof LOCAL_SETTINGS_WITH_DEFAULTS[name] === 'object' &&
          LOCAL_SETTINGS_WITH_DEFAULTS[name] !== null
        ) {
          return new Proxy(
            {}, // because this interfaces with localStorage, it does not actually need to proxy to an existing object
            {
              get(_, subpropertyName) {
                // get the subproperty from localstorage, or from the defaults config, or fall back on null
                let value = getValue(
                  makeKey(name, subpropertyName),
                  () => LOCAL_SETTINGS_WITH_DEFAULTS[name]?.[subpropertyName],
                );

                if (typeof value === 'function') {
                  value = value();
                }

                // store the value immediately, whatever it is, on get. this is necessary for properties like "deviceUUID" in which a dynamic default value must persist
                setValue(makeKey(name, subpropertyName), value);

                return value;
              },
              set(_, subpropertyName, value) {
                // set the subproperty to the passed-in value
                setValue(makeKey(name, subpropertyName), value);

                return true;
              },
            },
          );
        }

        // get the property from localstorage, or from the default config, or fall back on null
        let value = getValue(
          makeKey(name),
          () => LOCAL_SETTINGS_WITH_DEFAULTS[name],
        );

        if (typeof value === 'function') {
          value = value();
        }

        // store the value immediately, whatever it is, on get. this is necessary for properties like "deviceUUID" in which a dynamic default value must persist
        setValue(makeKey(name), value);

        return value;
      },
      set(_, name, value) {
        // set the property to the passed-in value
        setValue(makeKey(name), value);

        return true;
      },
    },
  );
};

export default getLocalSettingsProxy;
